import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, HandHeart, Wind } from "lucide-react";
import Card from "../components/ui/Card";
import Pill from "../components/ui/Pill";
import SurahImage from "../components/ui/SurahImage";
import { TEMPTATION_ACTIONS } from "../lib/constants";

function BreathingMini({ seconds = 30 }) {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    setTimeLeft(seconds);
    setHasCompleted(false);

    const id = setInterval(() => {
      setTimeLeft((x) => {
        const next = x - 1;
        if (next <= 0) {
          setHasCompleted(true);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [seconds]);

  // 4 inhale, 2 hold, 6 exhale = 12s cycle
  const phase = useMemo(() => {
    if (hasCompleted) return "Complete!";

    const elapsed = seconds - timeLeft;
    const m = elapsed % 12;
    if (m < 4) return "Inhale";
    if (m < 6) return "Hold";
    return "Exhale";
  }, [seconds, timeLeft, hasCompleted]);

  return (
    <div className="rounded-2xl bg-white/70 dark:bg-zinc-900/40 ring-1 ring-black/10 dark:ring-white/10 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Breathe slowly</div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          {hasCompleted ? "Done!" : `${timeLeft}s`}
        </div>
      </div>
      <div className="mt-3 text-3xl font-bold">{phase}</div>
      <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
        Inhale 4  Hold 2  Exhale 6
      </div>
      {hasCompleted && (
        <div className="mt-3 text-sm text-green-600 dark:text-green-400 font-semibold">
           Great job! How are you feeling?
        </div>
      )}
      {!hasCompleted && (
        <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          Urges rise and fall. You don't need to act on this.
        </div>
      )}
    </div>
  );
}

function ExitPrompt({ onYes, onContinue }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center sm:items-center justify-center p-4" style={{ paddingBottom: "env(safe-area-inset-bottom, 20px)" }}>
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-zinc-950 ring-1 ring-black/10 dark:ring-white/10 shadow-lg p-4">
        <div className="font-semibold text-zinc-900 dark:text-zinc-50">
          Has this helped at all?
        </div>
        <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
          Even a short pause can reduce an urge. You can return home if you feel
          better, or continue from here.
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={onYes}
            className="rounded-xl bg-green-600 hover:bg-green-700 text-white py-3 font-semibold transition-colors"
            aria-label="Confirm you feel better and go home"
          >
            Yes, I feel better
          </button>
          <button
            onClick={onContinue}
            className="rounded-xl bg-white dark:bg-zinc-900/50 ring-1 ring-black/10 dark:ring-white/10 py-3 font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-900/70 transition-colors"
            aria-label="Keep trying coping strategies"
          >
            Keep trying
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TemptedPage({ goHome }) {
  // step: 1 = breathe + bismillah, 2 = surahs, 3 = action
  const [step, setStep] = useState(1);

  // exit prompt visibility
  const [showExit, setShowExit] = useState(false);

  // chosen action in step 3
  const [choice, setChoice] = useState(null);

  // confirmation state for restart button
  const [restartConfirm, setRestartConfirm] = useState(false);

  // Back button handling - flag to prevent history stack growth
  const [attemptedExit, setAttemptedExit] = useState(false);

  // --- Back/Exit interception (Fixed: prevents memory leak) ---
  const handlePopState = useCallback(() => {
    if (!attemptedExit) {
      // First back press: show exit prompt
      setAttemptedExit(true);
      setShowExit(true);
    } else {
      // Second back press: actually navigate
      goHome?.();
    }
  }, [attemptedExit, goHome]);

  useEffect(() => {
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [handlePopState]);

  const requestExit = () => setShowExit(true);

  const exitYes = () => {
    setShowExit(false);
    goHome?.();
  };

  // Keep them on step 3 (or whatever step they're on), just close the modal
  const exitContinue = () => {
    setShowExit(false);
  };

  // Handle action choice with validation
  const handleActionChoice = useCallback((actionKey) => {
    setChoice(actionKey);
  }, []);

  // Handle submit with validation
  const handleSubmit = useCallback(() => {
    if (!choice) return; // Silent no-op if choice not selected
    setShowExit(true);
  }, [choice]);

  // Memoize action options
  const actionOptions = useMemo(() => TEMPTATION_ACTIONS, []);

  const handleRestart = useCallback(() => {
    setChoice(null);
    setStep(1);
    setRestartConfirm(false);
    setAttemptedExit(false);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-2 flex flex-col"
      style={{ paddingBottom: "calc(80px + env(safe-area-inset-bottom, 20px))" }}
    >
      {/* Top back/exit button */}
      <div className="flex items-center justify-between">
        <button
          onClick={requestExit}
          aria-label="Exit crisis intervention and go home"
          className="inline-flex items-center gap-2 rounded-xl bg-white/70 dark:bg-zinc-900/40 ring-1 ring-black/10 dark:ring-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/80 dark:hover:bg-zinc-900/50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Exit
        </button>

        <div 
          className="text-xs text-zinc-500 dark:text-zinc-400"
          role="status"
          aria-live="polite"
          aria-label={`Current step: ${step} of 3`}
        >
          Step {step}/3
        </div>
      </div>

      {/* STEP 1 */}
      {step === 1 ? (
        <Card title="Pause (Step 1/3)" icon={Wind}>
          <div className="text-sm text-zinc-600 dark:text-zinc-300">
            Say <span className="font-semibold">Bismillah</span>, then breathe slowly.
          </div>

          <div className="mt-3 rounded-2xl bg-white/70 dark:bg-zinc-900/40 ring-1 ring-black/10 dark:ring-white/10 p-4">
            <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">
              بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
            </div>
            <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
              Bismillāhir-Raḥmānir-Raḥīm
            </div>
            <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              In the name of Allah, the Most Merciful, the Most Compassionate.
            </div>
          </div>

          <div className="mt-3">
            <BreathingMini seconds={30} />
          </div>

          <button
            onClick={() => setStep(2)}
            className="mt-4 w-full rounded-xl bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-3 font-semibold hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors"
            aria-label="Proceed to step 2"
          >
            Next
          </button>
        </Card>
      ) : null}

      {/* STEP 2 */}
      {step === 2 ? (
        <Card title="Read for protection (Step 2/3)" icon={Wind}>
          <div className="text-sm text-zinc-600 dark:text-zinc-300">
            Read slowly. Repeat if needed.
          </div>

          <div className="mt-4 space-y-3">
            <SurahImage title="Surah Al-Falaq (113)" src="/Surah_Falaq.png" />
            <SurahImage title="Surah An-Nas (114)" src="/Surah_Naas.png" />
          </div>

          <button
            onClick={() => setStep(3)}
            className="mt-4 w-full rounded-xl bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-3 font-semibold hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors"
            aria-label="Proceed to step 3"
          >
            Next
          </button>
        </Card>
      ) : null}

      {/* STEP 3 */}
      {step === 3 ? (
        <Card title="Do one small action (Step 3/3)" icon={HandHeart}>
          <div className="text-sm text-zinc-600 dark:text-zinc-300">
            Pick one action and do it now. Small wins break the urge.
          </div>

          <div className="mt-3 flex flex-col gap-2">
            {actionOptions.map((o) => (
              <Pill
                key={o.key}
                active={choice === o.key}
                onClick={() => handleActionChoice(o.key)}
                role="button"
                aria-pressed={choice === o.key}
              >
                {o.label}
              </Pill>
            ))}
          </div>

          {!choice ? (
            <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Choose one action first.
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-2 gap-2">
            {restartConfirm ? (
              <>
                <button
                  onClick={() => setRestartConfirm(false)}
                  className="rounded-xl bg-white dark:bg-zinc-900/50 ring-1 ring-black/10 dark:ring-white/10 py-3 font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-900/70 transition-colors"
                  aria-label="Cancel restart"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRestart}
                  className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white py-3 font-semibold transition-colors"
                  aria-label="Confirm restart from step 1"
                >
                  Confirm restart
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setRestartConfirm(true)}
                  className="rounded-xl bg-white dark:bg-zinc-900/50 ring-1 ring-black/10 dark:ring-white/10 py-3 font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-900/70 transition-colors"
                  aria-label="Restart from beginning"
                >
                  Start again
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!choice}
                  className={`rounded-xl py-3 font-semibold transition-colors ${
                    choice
                      ? "bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-100"
                      : "bg-zinc-200 dark:bg-zinc-800 text-zinc-500 cursor-not-allowed"
                  }`}
                  aria-label={choice ? "Confirm you feel better" : "Select an action first"}
                >
                  Has it helped?
                </button>
              </>
            )}
          </div>

          <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            If you still feel the urge, restart and repeat. You're building control.
          </div>
        </Card>
      ) : null}

      {showExit ? <ExitPrompt onYes={exitYes} onContinue={exitContinue} /> : null}
    </motion.div>
  );
}
